import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Filter, Download, Check, X, Link as LinkIcon } from 'lucide-react';
import { useScenarios } from '../hooks/useScenarios';

const ScanHistory = ({ supabase }) => {
  // State for scans data
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get scenarios using the custom hook
  const { scenarios, loading: scenariosLoading } = useScenarios(supabase);
  
  // Filter state
  const [filters, setFilters] = useState({
    scenario: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  
  // Fetch scan data
  const fetchScans = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) return;
      
      // Start building the query
      let query = supabase
        .from('scans')
        .select(`
          id, 
          scanned_value, 
          message, 
          created_at, 
          result, 
          validate_record_url, 
          create_record_url,
          scenario_id,
          scenarios(name)
        `)
        .order('created_at', { ascending: false });
      
      // Apply filters if they exist
      if (filters.scenario) {
        query = query.eq('scenario_id', filters.scenario);
      }
      
      if (filters.status === 'success') {
        query = query.eq('result', 'success');
      } else if (filters.status === 'failure') {
        query = query.neq('result', 'success');
      }
      
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        query = query.gte('created_at', fromDate.toISOString());
      }
      
      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        // Add one day to include the end date
        toDate.setDate(toDate.getDate() + 1);
        query = query.lt('created_at', toDate.toISOString());
      }
      
      // Execute the query
      const { data, error: scanError } = await query;
      
      if (scanError) throw scanError;
      
      // Format the scan data
      const formattedScans = data.map(scan => ({
        id: scan.id,
        value: scan.scanned_value,
        success: scan.result === 'success',
        timestamp: scan.created_at,
        scenarioId: scan.scenario_id,
        scenario: scan.scenarios?.name || 'Unknown',
        message: scan.message || '',
        validateUrl: scan.validate_record_url,
        createUrl: scan.create_record_url
      }));
      
      setScans(formattedScans);
      
    } catch (err) {
      console.error('Error fetching scans:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, supabase]);
  
  // Initial fetch and re-fetch when filters change
  useEffect(() => {
    fetchScans();
    
    // Subscribe to realtime changes
    const subscription = supabase
      .channel('scans_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'scans' 
        }, 
        () => {
          fetchScans();
        }
      )
      .subscribe();
      
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchScans, supabase]);
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const resetFilters = () => {
    setFilters({
      scenario: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
  };
  
  const exportCSV = async () => {
    try {
      // Get the current filtered scans
      const dataToExport = scans.map(scan => ({
        'Scan ID': scan.id,
        'Scanned Value': scan.value,
        'Status': scan.success ? 'Success' : 'Failed',
        'Scenario': scan.scenario,
        'Message': scan.message,
        'Timestamp': new Date(scan.timestamp).toLocaleString(),
        'Validation Record': scan.validateUrl || '',
        'Created Record': scan.createUrl || ''
      }));
      
      if (dataToExport.length === 0) {
        alert('No data to export');
        return;
      }
      
      // Convert to CSV
      const headers = Object.keys(dataToExport[0]);
      const csvContent = [
        headers.join(','),
        ...dataToExport.map(row => 
          headers.map(header => {
            // Handle commas and quotes in the data
            const cellData = String(row[header] || '');
            return cellData.includes(',') || cellData.includes('"') 
              ? `"${cellData.replace(/"/g, '""')}"` 
              : cellData;
          }).join(',')
        )
      ].join('\n');
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `qr2air-scan-history-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Failed to export data: ' + err.message);
    }
  };
  
  return (
    <div className="bg-gray-50">
      <main className="px-0">
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Scan History</h2>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">
              View and manage all QR code scan records
            </p>
            <button
              type="button"
              onClick={exportCSV}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>
      
        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
              <Filter className="h-5 w-5 mr-2" />
              Filter Scans
            </h3>
            
            <div className="mt-4 grid grid-cols-1 gap-y-4 sm:grid-cols-2 lg:grid-cols-4 gap-x-4">
              <div>
                <label htmlFor="scenario" className="block text-sm font-medium text-gray-700">Scenario</label>
                <select
                  id="scenario"
                  name="scenario"
                  value={filters.scenario}
                  onChange={handleFilterChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  disabled={scenariosLoading}
                >
                  <option value="">All Scenarios</option>
                  {scenarios.map(scenario => (
                    <option key={scenario.id} value={scenario.id}>{scenario.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  id="status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">All Status</option>
                  <option value="success">Success</option>
                  <option value="failure">Failure</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700">From Date</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    name="dateFrom"
                    id="dateFrom"
                    value={filters.dateFrom}
                    onChange={handleFilterChange}
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="dateTo" className="block text-sm font-medium text-gray-700">To Date</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    name="dateTo"
                    id="dateTo"
                    value={filters.dateTo}
                    onChange={handleFilterChange}
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-5 text-right">
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
        
        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500"></div>
          </div>
        )}
        
        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  Error loading scan data: {error}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Results Table */}
        {!loading && !error && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scanned Value
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scenario
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Message
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Record Used
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created Record
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {scans.map((scan) => (
                    <tr key={scan.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          scan.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {scan.success ? (
                            <Check className="mr-1 h-3 w-3" />
                          ) : (
                            <X className="mr-1 h-3 w-3" />
                          )}
                          {scan.success ? 'Success' : 'Failed'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {scan.value}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {scan.scenario}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {scan.message}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {scan.validateUrl ? (
                          <a 
                            href={scan.validateUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                          >
                            <LinkIcon className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">View Record</span>
                          </a>
                        ) : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {scan.createUrl ? (
                          <a 
                            href={scan.createUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                          >
                            <LinkIcon className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">View Record</span>
                          </a>
                        ) : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(scan.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {scans.length === 0 && (
              <div className="text-center py-6">
                <p className="text-gray-500">No scan records found with the current filters.</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ScanHistory;